import React, { useEffect, useState, useCallback } from 'react';
import Loading from '../Loading';
import { Upload } from 'models/upload';
import { ReceptionDTO } from 'models/reception';
import { TripCardDTO } from 'models/trip-card';
import { ReceptionItemKind } from 'enums/reception.enum';
import moment from 'moment';

type Props = {
    show: boolean;
    title?: string;
    loading?: boolean;
    initial?: ReceptionDTO | null;
    onSave: (reception: ReceptionDTO) => void;
    onClose: () => void;
};

interface AgentOption {
    name: string;
    isCustom: boolean;
}

interface TripFeeOption {
    type: 'province' | 'city';
    name: string;
    tripFee: number;
    tripCost: number;
}

const TripCardModal: React.FC<Props> = ({
    show,
    title = '출장카드',
    loading = false,
    initial,
    onSave,
    onClose,
}) => {
    const [form, setForm] = useState<ReceptionDTO | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [customAgent, setCustomAgent] = useState('');
    const [agentOptions, setAgentOptions] = useState<AgentOption[]>([]);
    const [loadingAgents, setLoadingAgents] = useState(false);
    const [useCustomAgent, setUseCustomAgent] = useState(false);

    // 출장카드 모달 ESC 핸들러
    useEffect(() => {
        const handleEscKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && show) {
                onClose();
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [show]);

    useEffect(() => {
        if (show) {
            loadAgentOptions();
        }
    }, [show]);

    const loadAgentOptions = async () => {
        setAgentOptions([
            { name: '전종수이사', isCustom: false },
            { name: '정찬권이사', isCustom: false },
            { name: '노승균', isCustom: false },
            { name: '강성인', isCustom: false },
            { name: '신용선', isCustom: false },
            { name: '유동수', isCustom: false },
            { name: '포항대리점', isCustom: false },
            { name: '제주소장', isCustom: false },
            { name: '직접입력', isCustom: true }
        ]);
        setLoadingAgents(false);
    };

    useEffect(() => {
        if (show && initial) {

            if (initial?.tripCard?.image && initial.tripCard.image.name) initial.tripCard.image._preview = initial.tripCard.image.name;

            setForm({
                ...initial,
                tripCard: initial.tripCard ? { ...initial.tripCard } : {
                    id: undefined,
                    tripDate: null,
                    agent: null,
                    content: null,
                    image: undefined,
                    imageUrl: null
                }
            });
            const currentAgent = initial.tripCard?.agent || '';
            if (currentAgent) {
                const inOptions = agentOptions.some(
                    (o) => !o.isCustom && o.name === currentAgent
                );
                setUseCustomAgent(!inOptions);
                setCustomAgent(!inOptions ? currentAgent : '');
            } else {
                setUseCustomAgent(false);
                setCustomAgent('');
            }
        } else if (show) {
            setForm(null);
            setUseCustomAgent(false);
            setCustomAgent('');
        }
    }, [initial, show, agentOptions]);

    const onTripCardChange = (name: keyof TripCardDTO, value: any) => {
        if (!form) return;
        setForm(prev => ({
            ...prev!,
            tripCard: {
                ...prev!.tripCard!,
                [name]: value
            }
        }));
    };

    const onReceptionChange = (name: keyof ReceptionDTO, value: any) => {
        if (!form) return;
        setForm(prev => ({
            ...prev!,
            [name]: value
        }));
    };

    const onAgentChange = (value: string) => {
        if (value === '직접입력') {
            setUseCustomAgent(true);
        } else {
            setUseCustomAgent(false);
            onTripCardChange('agent', value);
            setCustomAgent('');
        }
    };

    const onCustomAgentChange = (value: string) => {
        setCustomAgent(value);
        onTripCardChange('agent', value);
    };

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (file.size > 5 * 1024 * 1024) {
                alert('파일 크기는 5MB 이하로 제한됩니다.');
                return;
            }
            if (!file.type.startsWith('image/')) {
                alert('이미지 파일만 업로드 가능합니다.');
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const newImage = new Upload();
                newImage._preview = reader.result;
                newImage.name = file.name;
                newImage.size = file.size;
                newImage.value = reader.result;
                onTripCardChange('image', newImage);
            };
        }
    };

    const handleDrag = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            const file = e.dataTransfer.files[0];
            if (file.size > 5 * 1024 * 1024) {
                alert('파일 크기는 5MB 이하로 제한됩니다.');
                return;
            }
            if (!file.type.startsWith('image/')) {
                alert('이미지 파일만 업로드 가능합니다.');
                return;
            }
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const newImage = new Upload();
                newImage._preview = reader.result;
                newImage.name = file.name;
                newImage.size = file.size;
                newImage.value = reader.result;
                onTripCardChange('image', newImage);
            };
        }
    };

    const removeImage = () => {
        onTripCardChange('image', undefined);
    };

    const handleSave = () => {
        if (!form) return;
        if (!form.tripCard?.content) {
            alert('출장내용을 입력해주세요.');
            return;
        }
        onSave(form);
    };

    const handleClear = () => {
        if (window.confirm('출장카드 내용을 모두 지울까요?')) {
            if (!form) return;
            const clearedForm = {
                ...form,
                tripCard: null,
                tripFeeAmount: 0
            };
            onSave(clearedForm);
        }
    };

    const handleImageSaveWithPath = async () => {
        try {
            const html2canvas = (await import('html2canvas')).default;

            // 출력용 숨겨진 영역 선택 (더 깔끔한 출력을 위해)
            const element = document.getElementById('trip-card-print-area');
            if (!element) {
                console.error('출장카드 출력 영역을 찾을 수 없습니다.');
                alert('이미지를 생성할 영역을 찾을 수 없습니다.');
                return;
            }

            // html2canvas를 사용하여 캔버스 생성
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                allowTaint: true,
                logging: false,
                backgroundColor: '#ffffff',
                width: 1400,
                height: element.scrollHeight
            });

            const phone = form?.phone1 || '';
            const tripDate = form?.tripCard?.tripDate || moment().format('YYYY-MM-DD');
            const fileName = `출장카드_${phone}_${tripDate}.png`;

            // File System Access API 지원 확인 및 타입 단언
            if ('showSaveFilePicker' in window) {
                try {
                    const opts = {
                        suggestedName: fileName,
                        types: [{
                            description: 'PNG 이미지',
                            accept: { 'image/png': ['.png'] }
                        }]
                    };

                    const fileHandle = await (window as any).showSaveFilePicker(opts);

                    canvas.toBlob(async (blob) => {
                        if (blob) {
                            try {
                                const writable = await fileHandle.createWritable();
                                await writable.write(blob);
                                await writable.close();
                                alert('출장카드 이미지가 성공적으로 저장되었습니다.');
                            } catch (writeError) {
                                console.error('파일 쓰기 오류:', writeError);
                                alert('파일 저장 중 오류가 발생했습니다.');
                            }
                        }
                    }, 'image/png');

                } catch (err: any) {
                    // 사용자가 취소한 경우 무시
                    if (err.name !== 'AbortError') {
                        console.error('파일 선택 오류:', err);
                        alert('파일 선택 중 오류가 발생했습니다.');
                    }
                }
            } else {
                // File System Access API를 지원하지 않는 경우 기존 방식 사용
                console.log('File System Access API 미지원 - 기본 다운로드 폴더 사용');

                canvas.toBlob((blob) => {
                    if (blob) {
                        const url = URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.download = fileName;
                        link.href = url;
                        link.click();
                        URL.revokeObjectURL(url);
                        alert('출장카드 이미지가 다운로드되었습니다.');
                    }
                }, 'image/png');
            }

        } catch (error) {
            console.error('이미지 저장 실패:', error);
            alert('이미지 저장 중 오류가 발생했습니다.');
        }
    };

    if (!show || !form) return null;

    const customerName = form.customerName || '';
    const phone = form.phone1 || form.phone2 || form.phone3 || '';
    const address = `${form.address || ''}`.trim();
    const tripCard = form.tripCard || {} as TripCardDTO;
    const boilerItems = form.items?.filter(item => item.kind === ReceptionItemKind.BOILER) || [];

    return (
        <>
            <div
                className={`modal fade ${show ? 'show' : ''}`}
                style={{
                    display: show ? 'block' : 'none',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    zIndex: 1055
                }}
                aria-modal="true"
                role="dialog"
            >
                <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '1200px' }}>
                    <div className="modal-content">
                        <div className="modal-header" style={{ backgroundColor: '#ffc107', color: '#212529', padding: '15px 20px' }}>
                            <h5 className="modal-title" style={{ fontSize: '24px', fontWeight: '700' }}>
                                출장카드
                            </h5>
                            <div className="d-flex gap-2">
                                <button type="button" className="btn btn-outline-dark btn-sm" onClick={handleImageSaveWithPath} style={{ fontSize: '18px', padding: '8px 16px' }}>
                                    이미지저장
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} style={{ fontSize: '18px', padding: '8px 16px' }}>
                                    닫기
                                </button>
                                <button type="button" className="btn btn-dark btn-sm" onClick={handleSave} style={{ fontSize: '18px', padding: '8px 16px' }}>
                                    저장
                                </button>
                            </div>
                        </div>

                        <div className="modal-body" id="trip-card-content" style={{ padding: '20px', backgroundColor: '#ffffff', maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                            {(loading || loadingAgents) && <Loading isWhole={false} />}

                            <div className="row">
                                {/* 좌측 폼 영역 */}
                                <div className="col-md-7">
                                    {/* 접수일자, 출장일자, 출장자 */}
                                    <div className="row mb-3">
                                        <div className="col-md-3">
                                            <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>접수일자</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={form.receivedDate || ''}
                                                onChange={(e) => onReceptionChange('receivedDate', e.target.value)}
                                                style={{ fontSize: '20px', padding: '10px 14px', height: '50px' }}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>출장일자</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={tripCard.tripDate || ''}
                                                onChange={(e) => onTripCardChange('tripDate', e.target.value)}
                                                placeholder="yyyy-mm-dd"
                                                style={{ fontSize: '20px', padding: '10px 14px', height: '50px' }}
                                            />
                                        </div>
                                        <div className="col-md-3">
                                            <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>출장자</label>
                                            <select
                                                className="form-select"
                                                value={useCustomAgent ? '직접입력' : (tripCard.agent || '')}
                                                onChange={(e) => onAgentChange(e.target.value)}
                                                disabled={loadingAgents}
                                                style={{ fontSize: '20px', padding: '10px 14px', height: '50px' }}
                                            >
                                                <option value="">선택</option>
                                                {agentOptions.map(option => (
                                                    <option key={option.name} value={option.name}>
                                                        {option.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-3">
                                            {useCustomAgent && (
                                                <>
                                                    <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>&nbsp;</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder="출장자명 입력"
                                                        value={customAgent}
                                                        onChange={(e) => onCustomAgentChange(e.target.value)}
                                                        style={{ fontSize: '20px', padding: '10px 14px', height: '50px' }}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* 고객명, 연락처 */}
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                            <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>고객명</label>
                                            <input
                                                className="form-control"
                                                value={customerName}
                                                readOnly
                                                style={{ backgroundColor: '#f5f5f5', fontSize: '20px', padding: '10px 14px', height: '50px' }}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>연락처</label>
                                            <input
                                                className="form-control"
                                                value={phone}
                                                readOnly
                                                style={{ backgroundColor: '#f5f5f5', fontSize: '20px', padding: '10px 14px', height: '50px' }}
                                            />
                                        </div>
                                    </div>

                                    {/* 주소 */}
                                    <div className="row mb-3">
                                        <div className="col-12">
                                            <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>주소</label>
                                            <input
                                                className="form-control"
                                                value={address}
                                                readOnly
                                                style={{ backgroundColor: '#f5f5f5', fontSize: '20px', padding: '10px 14px', height: '50px' }}
                                            />
                                        </div>
                                    </div>

                                    {/* 출장 보일러 목록 */}
                                    <div className="mb-3">
                                        <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>출장 보일러 목록</label>
                                        <div className="border rounded p-3" style={{ backgroundColor: '#f5f5f5', minHeight: '120px' }}>
                                            {boilerItems.length > 0 ? (
                                                boilerItems.map((item, index) => (
                                                    <div key={index} className="row mb-2">
                                                        <div className="col-4">
                                                            <label style={{ fontSize: '18px', color: '#666', marginBottom: '5px', fontWeight: '600' }}>보일러명</label>
                                                            <div style={{ fontSize: '20px', padding: '8px 12px', backgroundColor: '#fff', borderRadius: '4px', minHeight: '45px', display: 'flex', alignItems: 'center' }}>
                                                                {item.modelName || '-'}
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <label style={{ fontSize: '18px', color: '#666', marginBottom: '5px', fontWeight: '600' }}>제조년월</label>
                                                            <div style={{ fontSize: '20px', padding: '8px 12px', backgroundColor: '#fff', borderRadius: '4px', minHeight: '45px', display: 'flex', alignItems: 'center' }}>
                                                                {item.manufactureMonth || '-'}
                                                            </div>
                                                        </div>
                                                        <div className="col-4">
                                                            <label style={{ fontSize: '18px', color: '#666', marginBottom: '5px', fontWeight: '600' }}>특이사항</label>
                                                            <div style={{ fontSize: '20px', padding: '8px 12px', backgroundColor: '#fff', borderRadius: '4px', minHeight: '45px', display: 'flex', alignItems: 'center' }}>
                                                                {item.note || '-'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-center text-muted py-3">
                                                    <span style={{ fontSize: '20px' }}>보일러 정보가 없습니다.</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* 출장내용 */}
                                    <div className="mb-3">
                                        <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '8px', display: 'block' }}>출장내용<span className="text-danger">*</span></label>
                                        <textarea
                                            className="form-control"
                                            rows={5}
                                            value={tripCard.content || ''}
                                            onChange={(e) => onTripCardChange('content', e.target.value)}
                                            placeholder="증상/요청사항 등"
                                            style={{ fontSize: '20px', padding: '12px 14px', lineHeight: '1.6' }}
                                        />
                                    </div>
                                </div>

                                {/* 우측 이미지 업로드 영역 */}
                                <div className="col-md-5">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label style={{ fontSize: '22px', fontWeight: '700', marginBottom: '0' }}>이미지업로드</label>
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary"
                                            onClick={removeImage}
                                            disabled={!tripCard.image?._preview}
                                            style={{ fontSize: '18px', padding: '6px 14px' }}
                                        >
                                            삭제
                                        </button>
                                    </div>
                                    <div
                                        className={`border-2 border-dashed rounded d-flex align-items-center justify-content-center position-relative ${dragActive ? 'border-primary' : 'border-secondary'}`}
                                        onDragEnter={handleDrag}
                                        onDragLeave={handleDrag}
                                        onDragOver={handleDrag}
                                        onDrop={handleDrop}
                                        onClick={() => document.getElementById('imageFileInput')?.click()}
                                        style={{
                                            height: '420px',
                                            cursor: 'pointer',
                                            backgroundColor: dragActive ? '#f0f8ff' : '#f5f5f5'
                                        }}
                                    >
                                        <input
                                            id="imageFileInput"
                                            type="file"
                                            accept="image/*"
                                            onChange={onFileChange}
                                            style={{ display: 'none' }}
                                        />

                                        {tripCard.image?._preview ? (
                                            <img
                                                src={tripCard.image._preview || tripCard.image.value}
                                                alt="업로드된 이미지"
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'contain',
                                                    padding: '10px'
                                                }}
                                            />
                                        ) : (
                                            <div className="text-center text-muted">
                                                <div style={{ fontSize: '64px', marginBottom: '15px', color: '#999' }}>
                                                    <i className="fas fa-camera"></i>
                                                </div>
                                                <div style={{ fontSize: '22px', fontWeight: '700', marginBottom: '10px' }}>
                                                    이미지업로드
                                                </div>
                                                <div style={{ fontSize: '18px', color: '#666' }}>
                                                    클릭 또는 파일 드래그
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="modal-footer" style={{ padding: '12px 20px' }}>
                            <button
                                type="button"
                                className="btn btn-outline-danger me-auto"
                                onClick={handleClear}
                                style={{ fontSize: '18px', padding: '8px 16px' }}
                            >
                                <i className="fas fa-eraser mr-1" />
                                내용 초기화
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {show && <div className="modal-backdrop fade show" style={{ zIndex: 1050 }} />}

            {/* 이미지 출력용 숨겨진 영역 - 글자 크기 2배 증가 */}
            {show && (
                <div
                    id="trip-card-print-area"
                    style={{
                        position: 'fixed',
                        left: '-9999px',
                        top: '0',
                        width: '1400px',
                        backgroundColor: '#ffffff',
                        padding: '40px'
                    }}
                >
                    <div className="row">
                        {/* 좌측 폼 영역 */}
                        <div className="col-md-8">
                            {/* 접수일자, 출장일자, 출장자 */}
                            <div className="row mb-4">
                                <div className="col-md-4">
                                    <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>접수일자</label>
                                    <div style={{ fontSize: '48px', fontWeight: '700', padding: '24px', border: '3px solid #333', borderRadius: '6px', backgroundColor: '#fff', minHeight: '100px', display: 'flex', alignItems: 'center' }}>
                                        {form.receivedDate || '-'}
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>출장일자</label>
                                    <div style={{ fontSize: '48px', fontWeight: '700', padding: '24px', border: '3px solid #333', borderRadius: '6px', backgroundColor: '#fff', minHeight: '100px', display: 'flex', alignItems: 'center' }}>
                                        {tripCard.tripDate || '-'}
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>출장자</label>
                                    <div style={{ fontSize: '48px', fontWeight: '700', padding: '24px', border: '3px solid #333', borderRadius: '6px', backgroundColor: '#fff', minHeight: '100px', display: 'flex', alignItems: 'center' }}>
                                        {tripCard.agent || '-'}
                                    </div>
                                </div>
                            </div>

                            {/* 고객명, 연락처 */}
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>고객명</label>
                                    <div style={{ backgroundColor: '#f5f5f5', fontSize: '48px', fontWeight: '700', padding: '24px', border: '3px solid #333', borderRadius: '6px', minHeight: '100px', display: 'flex', alignItems: 'center' }}>
                                        {customerName || '-'}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>연락처</label>
                                    <div style={{ backgroundColor: '#f5f5f5', fontSize: '48px', fontWeight: '700', padding: '24px', border: '3px solid #333', borderRadius: '6px', minHeight: '100px', display: 'flex', alignItems: 'center' }}>
                                        {phone || '-'}
                                    </div>
                                </div>
                            </div>

                            {/* 주소 */}
                            <div className="row mb-4">
                                <div className="col-12">
                                    <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>주소</label>
                                    <div style={{ backgroundColor: '#f5f5f5', fontSize: '48px', fontWeight: '700', padding: '24px', border: '3px solid #333', borderRadius: '6px', minHeight: '100px', display: 'flex', alignItems: 'center' }}>
                                        {address || '-'}
                                    </div>
                                </div>
                            </div>

                            {/* 출장 보일러 목록 */}
                            <div className="mb-4">
                                <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>출장 보일러 목록</label>
                                <div className="border rounded p-4" style={{ backgroundColor: '#f5f5f5', minHeight: '180px', border: '3px solid #333' }}>
                                    {boilerItems.length > 0 ? (
                                        boilerItems.map((item, index) => (
                                            <div key={index} className="row mb-3">
                                                <div className="col-4">
                                                    <label style={{ fontSize: '40px', fontWeight: '800', color: '#000', marginBottom: '10px' }}>보일러명</label>
                                                    <div style={{ fontSize: '44px', fontWeight: '700', padding: '18px', backgroundColor: '#fff', borderRadius: '6px', border: '2px solid #666', minHeight: '90px', display: 'flex', alignItems: 'center' }}>
                                                        {item.modelName || '-'}
                                                    </div>
                                                </div>
                                                <div className="col-4">
                                                    <label style={{ fontSize: '40px', fontWeight: '800', color: '#000', marginBottom: '10px' }}>제조년월</label>
                                                    <div style={{ fontSize: '44px', fontWeight: '700', padding: '18px', backgroundColor: '#fff', borderRadius: '6px', border: '2px solid #666', minHeight: '90px', display: 'flex', alignItems: 'center' }}>
                                                        {item.manufactureMonth || '-'}
                                                    </div>
                                                </div>
                                                <div className="col-4">
                                                    <label style={{ fontSize: '40px', fontWeight: '800', color: '#000', marginBottom: '10px' }}>특이사항</label>
                                                    <div style={{ fontSize: '44px', fontWeight: '700', padding: '18px', backgroundColor: '#fff', borderRadius: '6px', border: '2px solid #666', minHeight: '90px', display: 'flex', alignItems: 'center' }}>
                                                        {item.note || '-'}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center text-muted py-4">
                                            <span style={{ fontSize: '44px', fontWeight: '700' }}>보일러 정보가 없습니다.</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 출장내용 */}
                            <div className="mb-4">
                                <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>출장내용</label>
                                <div style={{ fontSize: '48px', fontWeight: '700', minHeight: '250px', whiteSpace: 'pre-wrap', padding: '24px', border: '3px solid #333', borderRadius: '6px', backgroundColor: '#fff', lineHeight: '1.6' }}>
                                    {tripCard.content || '-'}
                                </div>
                            </div>
                        </div>

                        {/* 우측 이미지 영역 */}
                        <div className="col-md-4">
                            <label style={{ fontSize: '52px', fontWeight: '800', marginBottom: '15px', display: 'block', color: '#000' }}>이미지</label>
                            <div
                                className="border rounded d-flex align-items-center justify-content-center"
                                style={{
                                    height: '500px',
                                    backgroundColor: '#f5f5f5',
                                    border: '3px solid #333'
                                }}
                            >
                                {tripCard.image?._preview ? (
                                    <img
                                        src={tripCard.image._preview || tripCard.image.value}
                                        alt="출장카드 이미지"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            padding: '15px'
                                        }}
                                    />
                                ) : (
                                    <div className="text-center text-muted">
                                        <span style={{ fontSize: '34px', fontWeight: '700' }}>이미지 없음</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                </div>
            )}
        </>
    );
};

export default TripCardModal;